<template>
    <div id="balance-v2-page">
        <ModalRechargeHistory ref="modalRecharge" :credits="credits" :place-current-credits-per-month="place_current_credits_per_month" />
        
        <ModalAdjustMonthly 
            ref="modalAdjust" 
            :user="$store.state.user" 
            :place="$store.state.place"
            :plan="plan" 
            :initialCredits="credits_per_month" 
            :initialPrice="price_monthly_credits"
            :saving="saving"
            @submit="updateCreditsPerMonth" 
        />
        
        <ModalExtraPackage 
            ref="modalExtra" 
            :user="$store.state.user" 
            :place="$store.state.place"
            :plan="plan" 
            :saving="saving"
            @success="refreshData" 
        />
        <ModalStatement ref="modalStatement" />

        <div class="container-fluid py-4">
            <div v-if="initialDataError" class="alert alert-danger mb-4">
                {{ t('balance-load_error') }}
            </div>

            <div v-if="filtersReady" class="mb-4">
                <KvFilter 
                    :defaultSelected="['period']" 
                    ref="filterBar" 
                    @changed="updateFilters" 
                    :show-labels="true" 
                    :has-period-filter="false" 
                    :has-branch-filter="true" 
                    :filters="extraFilters" 
                />
                <div class="clearfix"></div>
            </div>

            <div class="balance-top">
                <div class="overview-col">
                    <div class="totals-grid">
                        <Card
                            v-for="(card, index) in summaryCards"
                            :key="index"
                            :label="card.label"
                            :value="card.value"
                            :icon="card.icon"
                            :color="card.color"
                            :loading="usageStatus === 'loading'"
                        />
                    </div>

                    <div class="row stretch-row donuts-row" v-if="usageStatus === 'success' || usageStatus === 'loading'">
                        <div :class="showBranchChart ? 'col-md-6' : 'col-md-12'" class="mb-3 mb-md-0 d-flex">
                            <div class="balance-card w-100 mb-0 d-flex flex-column">
                                <ConsumptionDonutChart
                                    :title="t('balance-consumption_by_category')"
                                    :chartData="formattedCategoryData"
                                    :loading="usageStatus === 'loading'"
                                    class="flex-grow-1"
                                />
                            </div>
                        </div>
                        <div v-if="showBranchChart" class="col-md-6 d-flex">
                            <div class="balance-card w-100 mb-0 d-flex flex-column">
                                <ConsumptionDonutChart
                                    :title="t('balance-consumption_by_branch')"
                                    :chartData="formattedBranchData"
                                    :loading="usageStatus === 'loading'"
                                    class="flex-grow-1"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="sidebar-col">
                    <div class="balance-card sidebar-card mb-0">
                        <BalanceSummary 
                            :loading="loadingBalance"
                            :balance="balance"
                            :credits="credits"
                            :credits-per-month="credits_per_month"
                            :price-monthly-credits="price_monthly_credits"
                            :place-current-credits-per-month="place_current_credits_per_month"
                            :plan="plan"
                            :automationsUsage="automationsUsage"
                            @refresh="refreshData"
                            @open-recharge-history="$refs.modalRecharge.open()"
                            @open-adjust-monthly="$refs.modalAdjust.open()"
                            @open-extra-package="$refs.modalExtra.open()"
                        />
                        <hr class="my-3" />
                        <CostTable 
                            :loading="loadingPlan"
                            :creditsCost="creditsCost" 
                            :automationsUsage="automationsUsage"
                        />
                    </div>
                </div>
            </div>

            <div v-show="usageStatus !== 'error'">
                <div class="balance-card">
                    <div v-if="usageDataError" class="alert alert-warning mb-3">
                        {{ t('balance-usage_history_error') }}
                    </div>
                    <UsageHistory 
                        v-if="usageStatus === 'success' || usageStatus === 'loading'"
                        :loading="usageStatus === 'loading'"
                        :chartData="dailyUsageData"
                        :usageDailyAverage="usageDailyAverage"
                        :filters="filters"
                        @open-statement="$refs.modalStatement.open(filters)"
                        @group-by-changed="(val) => updateFilters({ group_by: val })"
                    />
                </div>

                <div class="balance-card">
                    <ConsumptionActions 
                        :filters="filters"
                        :total-consumed="reportSummary.totalConsumed"
                    />
                </div>
            </div>

            <div v-if="usageStatus === 'empty'" class="alert alert-info">
                {{ t('balance-not_enough_data') }}
            </div>

            <div v-if="usageStatus === 'error'" class="alert alert-danger">
                {{ t('balance-data_load_error') }}
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import keviApi from "../keviApi.js";
import BalanceSummary from "./components/balance-summary.vue";
import CostTable from "./components/cost-table.vue";
import UsageHistory from "./components/actions-usage-history.vue";
import Card from "./components/card.vue";
import ConsumptionActions from "./components/consumption-actions.vue";
import ConsumptionDonutChart from "./components/consumption-donut-chart.vue";
import KvFilter from "../components/reports/kv-filter-bar.vue";
import FormatMixin from "./mixins/format-mixin.js";
import { ACTION_TYPE_I18N_MAP, ACTION_TYPE_COLORS_MAP, DYNAMIC_COLORS_POOL } from "./utils/format.js";
import { isGroupByAllowed } from "./utils/group-by.js";

import ModalRechargeHistory from "./components/modal-recharge-history.vue";
import ModalAdjustMonthly from "./components/modal-adjust-monthly.vue";
import ModalExtraPackage from "./components/modal-extra-package.vue";
import ModalStatement from "./components/modal-statement.vue";

export default {
    mixins: [FormatMixin],
    components: { 
        BalanceSummary,
        CostTable,
        UsageHistory,
        Card,
        ConsumptionActions,
        ConsumptionDonutChart,
        KvFilter,
        ModalRechargeHistory,
        ModalAdjustMonthly,
        ModalExtraPackage,
        ModalStatement
    },
    data() {
        return {
            loadingBalance: true,
            loadingPlan: true,
            initialDataError: false,
            usageDataError: false,
            usageStatus: 'loading',
            balance: 0,
            credits_per_month: 0,
            price_monthly_credits: 0,
            place_current_credits_per_month: 0,
            filters: {},
            filtersReady: true,
            extraFilters: [],
            credits: [],
            plan: {},
            creditsCost: {},
            dailyUsageData: { labels: [], datasets: [] },
            categoryData: {},
            branchData: {},
            reportSummary: {},
            automationsUsage: { automations: [], campaigns: [] },
            usageRequestId: 0,
            saving: false
        };
    },
    created() {
        const permissions = this.$store.state.user?.permissions || {};
        const hasPermission = permissions.balance?.enabled == 1;

        if (!hasPermission) {
            this.$router.replace('/');
            return;
        }

        this.extraFilters = [
            {
                component: "period",
                label: this.t('balance-filter_by_date'),
                name: "period",
                removable: false,
                options: { maxRangeMonths: 12 }
            },
            {
                name: 'type',
                label: this.t('balance-action_type_filter'),
                component: 'kv-select',
                removable: true,
                options: [
                    { value: 'all', label: this.t('balance-all_actions') },
                    { value: 'automation', label: this.t('balance-automations_filter') },
                    { value: 'campaign', label: this.t('balance-campaigns_filter') }
                ]
            },
            {
                name: 'status',
                label: this.t('balance-status_filter'),
                component: "kv-select",
                removable: true,
                options: [
                    { value: "active", label: this.t('balance-active') },
                    { value: "inactive", label: this.t('balance-inactive') },
                ],  
            },
            {
                name: 'segment_id',
                label: this.t('balance-segments'),
                component: "checkbox",
                removable: true,
                options: {
                    options: []
                },
            }
        ];

        this.initFiltersFromUrl();
        this.loadAllInitialData();
    },
    mounted() {
        document.getElementsByTagName("title")[0].innerText = "Kevi | " + this.t('balance-page_title');
    },
    computed: {
        showBranchChart() {
            return Object.keys(this.branchData).length > 1 || this.usageStatus === 'loading';
        },
        summaryCards() {
            const summary = this.reportSummary || {};
            return [
                {
                    label: this.t('balance-total_consumed'),
                    value: summary.totalConsumed || 0,
                    icon: 'ui-bar-chart-10',
                    color: '#2AAA4C'
                },
                {
                    label: this.t('balance-in_automations'),
                    value: summary.automationsTotal || 0,
                    icon: 'ui-settings-02',
                    color: '#2AAA4C'
                },
                {
                    label: this.t('balance-in_campaigns'),
                    value: summary.campaignsTotal || 0,
                    icon: 'ui-send-01',
                    color: '#2AAA4C'
                }
            ];
        },
        usageDailyAverage() {
            if (this.reportSummary && this.reportSummary.avgDaily != null) {
                return this.formatNumber(this.reportSummary.avgDaily, 2);
            }
            return "0";
        },
        formattedCategoryData() {
            if (!this.categoryData || Object.keys(this.categoryData).length === 0) {
                return { labels: [], datasets: [] };
            }

            const labelMap = {};

            Object.entries(this.categoryData).forEach(([key, value]) => {
                const normalizedKey = key.toLowerCase().trim();
                const translationKey = ACTION_TYPE_I18N_MAP[normalizedKey] || 'balance-action_type_other';
                const label = this.t(translationKey);
                const color = ACTION_TYPE_COLORS_MAP[normalizedKey] || '#9CA3AF';
                
                if (!labelMap[label]) {
                    labelMap[label] = { value: 0, color };
                }
                labelMap[label].value += (parseFloat(value) || 0);
            });

            const labels = Object.keys(labelMap);
            const data = labels.map(l => labelMap[l].value);
            const backgroundColors = labels.map(l => labelMap[l].color);

            return {
                labels,
                datasets: [{
                    data,
                    backgroundColor: backgroundColors,
                    borderWidth: 0
                }]
            };
        },
        formattedBranchData() {
            if (!this.branchData || Object.keys(this.branchData).length === 0) {
                return { labels: [], datasets: [] };
            }

            const labels = Object.keys(this.branchData).map(
                key => key === 'Unknown' ? this.t('balance-unknown') : key
            );
            const data = Object.values(this.branchData);
            
            const backgroundColors = labels.map((_, i) => {
                return DYNAMIC_COLORS_POOL[i % DYNAMIC_COLORS_POOL.length];
            });

            return {
                labels,
                datasets: [{
                    data,
                    backgroundColor: backgroundColors,
                    borderWidth: 0
                }]
            };
        }
    },
    methods: {
        initFiltersFromUrl() {
            const query = this.$route.query;
            let filters = {};
            if (query.filter) {
                try {
                    filters = JSON.parse(query.filter);
                } catch (e) {
                    filters = {};
                }
            }
            if (!filters.period) {
                filters.period = {
                    start: moment().subtract(30, 'days').format('YYYY-MM-DD'),
                    end: moment().format('YYYY-MM-DD')
                }
            }
            this.dropInvalidGroupBy(filters);
            this.filters = filters;
        },
        dropInvalidGroupBy(filters) {
            if (!filters.group_by || !filters.period) return;
            const days = moment(filters.period.end).diff(moment(filters.period.start), 'days') + 1;
            if (!isGroupByAllowed(filters.group_by, days)) {
                delete filters.group_by;
            }
        },
        updateFilters(newFilters) {
            const oldFiltersStr = JSON.stringify(this.filters);
            let tempFilters;
            
            if (newFilters.group_by !== undefined && Object.keys(newFilters).length === 1) {
                tempFilters = { ...this.filters, ...newFilters };
            } else {
                const groupBy = this.filters.group_by;
                tempFilters = { ...newFilters };
                
                if (groupBy) {
                    tempFilters.group_by = groupBy;
                }

                if (!tempFilters.period) {
                    tempFilters.period = {
                        start: moment().subtract(30, 'days').format('YYYY-MM-DD'),
                        end: moment().format('YYYY-MM-DD')
                    };
                }

                this.dropInvalidGroupBy(tempFilters);
            }

            if (JSON.stringify(tempFilters) !== oldFiltersStr) {
                this.filters = tempFilters;
                this.syncFiltersToUrl();
                this.loadReport();
            }
        },
        syncFiltersToUrl() {
            this.$nextTick(() => {
                const query = { ...this.$route.query };
                if (Object.keys(this.filters).length > 0) {
                    query.filter = JSON.stringify(this.filters);
                } else {
                    delete query.filter;
                }
                this.$router.replace({ query }).catch(() => {});
            });
        },
        loadAllInitialData() {
            this.initialDataError = false;
            this.loadBalanceData();
            this.loadPlanData();
            this.loadAutomationsUsage();
            this.loadSegments();
            this.loadReport();
        },
        refreshData() {
            this.loadBalanceData();
            this.loadPlanData();
            this.loadAutomationsUsage();
            this.loadReport();
        },
        async loadBalanceData() {
            this.loadingBalance = true;
            try {
                const [balanceRes, creditsRes] = await Promise.all([
                    this.$http(keviApi.getOptions("transactions/get-balance", "GET")),
                    this.$http(keviApi.getOptions("transactions/credits", "GET"))
                ]);

                if (balanceRes && balanceRes.status === 200) {
                    this.balance = parseFloat(balanceRes.body.balance) || 0;
                    this.credits_per_month = parseFloat(balanceRes.body.credits_per_month) || 0;
                    this.price_monthly_credits = parseFloat(balanceRes.body.price_monthly_credits) || 0;
                    this.place_current_credits_per_month = parseFloat(balanceRes.body.credits_per_month) || 0;
                }

                if (creditsRes && creditsRes.status === 200 && creditsRes.body && creditsRes.body.credits) {
                    this.credits = Object.values(creditsRes.body.credits);
                }
            } catch (err) {
                console.error("Error loading balance and credits:", err);
                this.initialDataError = true;
                this.$store.dispatch('notification/error', this.t('balance-balance_load_error'));
            } finally {
                this.loadingBalance = false;
            }
        },
        async loadPlanData() {
            this.loadingPlan = true;
            try {
                const planRes = await this.$http(keviApi.getOptions("place/myplan", "GET"));
                if (planRes && planRes.status === 200) {
                    this.plan = planRes.body.placePlan;
                    this.creditsCost = planRes.body.creditsCost;
                }
            } catch (err) {
                console.error("Error loading plan and costs:", err);
            } finally {
                this.loadingPlan = false;
            }
        },
        async loadAutomationsUsage() {
            try {
                const usageRes = await this.$http(keviApi.getOptions("automations/credits-usage", "GET"));
                if (usageRes && usageRes.status === 200) {
                    this.automationsUsage = usageRes.body;
                }
            } catch (err) {
                console.error("Error loading automations metrics:", err);
            }
        },
        async loadSegments() {
            try {
                const segmentsRes = await this.$http(keviApi.getOptions("segments", "GET"));
                if (segmentsRes && segmentsRes.status === 200 && Array.isArray(segmentsRes.body)) {
                    const segmentFilter = this.extraFilters.find(f => f.name === 'segment_id');
                    if (segmentFilter) {
                        segmentFilter.options.options = segmentsRes.body.map(s => ({
                            value: s.id,
                            label: s.name
                        }));
                    }
                }
            } catch (err) {
                console.error("Error loading segments filter:", err);
            }
        },
        async loadReport() {
            if (!this.filters.period) return;
            this.usageDataError = false;
            this.usageStatus = 'loading';

            const currentId = ++this.usageRequestId;

            const options = keviApi.getOptions("transactions/report", "GET", { filters: this.filters });
            try {
                const response = await this.$http(options);
                if (currentId === this.usageRequestId) {
                    this.processReportResponse(response);
                }
            } catch (err) {
                if (currentId === this.usageRequestId) {
                    this.usageStatus = 'error';
                    this.usageDataError = true;
                    console.error("Error loading consumption report:", err);
                }
            }
        },
        processReportResponse(response) {
            if (response && response.status === 200) {
                const body = response.body;

                this.dailyUsageData = {
                    labels: body.labels,
                    rawLabels: body.rawLabels,
                    datasets: body.datasets,
                    group_by: body.group_by || 'day',
                };

                this.categoryData = body.categories || {};
                
                const rawBranches = body.branches || {};
                const translatedBranches = {};
                for (const [name, value] of Object.entries(rawBranches)) {
                    const label = name === '__unassigned__' ? this.t('balance-no_branch') : name;
                    translatedBranches[label] = value;
                }
                this.branchData = translatedBranches;

                this.reportSummary = body.summary || {};
                
                if (!body.rawLabels || body.rawLabels.length === 0) {
                    this.usageStatus = 'empty';
                } else {
                    this.usageStatus = 'success';
                }
            } else {
                this.usageStatus = 'error';
            }
        },
        updateCreditsPerMonth(payload) {
            if (!this.$store.state.user?.global_admin) return;
            this.saving = true;
            const options = keviApi.getOptions("transactions/credits-per-month", "post", {
                credits_per_month: payload.credits_per_month,
                price_monthly_credits: this.rawMoney(payload.price_monthly_credits),
                apply_monthly_change_now: payload.apply_monthly_change_now,
            });
            this.$http(options).then((response) => {
                this.saving = false;
                if (response.status === 200) {
                    this.$store.dispatch("notification/success", this.t('balance-monthly_plan_updated'));
                    this.$refs.modalAdjust.close();
                    this.refreshData();
                }
            }).catch(err => {
                this.saving = false;
                console.error(err);
                this.$store.dispatch("notification/error", this.t('balance-monthly_plan_update_error'));
            });
        },
        rawMoney(value) {
            return value ? parseFloat(value.toString().replace(/\./g, "").replace(",", ".").replace(/[^0-9.]/gm, "")) : 0;
        }
    }
};
</script>

<style scoped lang="scss">
#balance-v2-page {
    margin: 0 20px 20px 20px;
    min-height: calc(100vh - 80px);

    @media (max-width: 768px) {
        margin: 0 10px 10px 10px;
        
        .container-fluid {
            padding-left: 10px;
            padding-right: 10px;
        }
    }
}

.balance-top {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 1238px) {
        grid-template-columns: minmax(0, 1fr);

        .sidebar-col {
            order: -1;
        }
    }
}

.overview-col {
    display: flex;
    flex-direction: column;
    min-width: 0;

    .totals-grid {
        flex-shrink: 0;
    }

    .donuts-row {
        flex: 1 1 auto;

        > [class*='col-'] > .balance-card {
            flex: 1 1 auto;
        }
    }
}

.sidebar-col {
    display: flex;
    min-width: 0;

    .sidebar-card {
        width: 100%;
    }
}

.balance-card {
    background-color: #fff;
    border: 1px solid #E2E8F0;
    border-radius: 16px;
    padding: 20px 24px;
    margin-bottom: 16px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.02);
    display: flex;
    flex-direction: column;

    @media (max-width: 768px) {
        padding: 16px;
        margin-bottom: 16px;
    }

    @media (max-width: 576px) {
        padding: 14px 16px;
        border-radius: 12px;
    }
}

.stretch-row {
    display: flex;
    align-items: stretch;
    margin-left: -8px;
    margin-right: -8px;
    
    > [class*='col-'] {
        display: flex;
        flex-direction: column;
        padding-left: 8px;
        padding-right: 8px;
    }

    @media (max-width: 767px) {
        flex-direction: column;
        
        > [class*='col-'] {
            margin-bottom: 16px;
            
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}

.totals-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 16px;

    @media (max-width: 991px) {
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 12px;
    }

    @media (max-width: 576px) {
        grid-template-columns: 1fr;
        gap: 8px;
        margin-bottom: 12px;
    }
}

</style>
